import { useState, useEffect, useRef } from 'react';
import { authController } from '../../controllers/authController';

/* ─────────────────────────────────────────────
   OTP Verification (Login / Register / Google — teeno mein use hota hai)

   Do steps:
   1) 'phone' — user ke paas number nahi hai (challenge.phoneRequired) to number lo — login / register / Google teeno mein
   2) 'otp'   — number par gaya 6-digit OTP lo, verify karo

   Props:
   - challenge  : { challengeId, phoneMasked?, phoneRequired?, retryAfterSeconds? } (backend ka response)
   - onVerified : (result) => void   — OTP sahi; result.data mein token + user (localStorage mein save ho chuka)
   - onExpired  : () => void         — session khatam (10 min / zyada galat attempts) — dobara login karna hoga
   - onBack     : () => void
   - backLabel  : back button ka text
───────────────────────────────────────────── */
const o = {
  title: { fontSize: '26px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0', textAlign: 'center' },
  sub: { fontSize: '14px', color: '#6b7280', lineHeight: '1.5', margin: '0 0 20px 0', textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  label: { fontSize: '14px', fontWeight: '500', color: '#374151' },
  input: {
    padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
    fontSize: '16px', outline: 'none', transition: 'border-color 0.2s',
    width: '100%', boxSizing: 'border-box',
  },
  otpInput: {
    padding: '14px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
    fontSize: '24px', letterSpacing: '10px', textAlign: 'center', fontWeight: '600',
    outline: 'none', transition: 'border-color 0.2s', width: '100%', boxSizing: 'border-box',
  },
  btn: {
    backgroundColor: '#2563eb', color: '#fff', padding: '14px', borderRadius: '8px',
    border: 'none', fontWeight: '600', fontSize: '16px', cursor: 'pointer', width: '100%',
  },
  btnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  linkBtn: {
    background: 'transparent', border: 'none', color: '#2563eb', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', textDecoration: 'underline', padding: 0,
  },
  linkDisabled: { color: '#9ca3af', cursor: 'not-allowed', textDecoration: 'none' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  error: {
    backgroundColor: '#fee2e2', color: '#dc2626', padding: '10px', borderRadius: '8px',
    fontSize: '13px', textAlign: 'center', marginBottom: '14px',
  },
  info: {
    backgroundColor: '#dcfce7', color: '#166534', padding: '10px', borderRadius: '8px',
    fontSize: '13px', textAlign: 'center', marginBottom: '14px',
  },
};

// Ye errors ka matlab: challenge khatam, dobara login karna padega
const isSessionDead = (r) =>
  ['CHALLENGE_EXPIRED', 'RESEND_LIMIT', 'PHONE_CHANGE_LIMIT'].includes(r?.errorCode) ||
  /login again/i.test(r?.message || '');

const digitsOnly = (v) => String(v || '').replace(/\D/g, '');

const OtpVerification = ({ challenge, onVerified, onExpired, onBack, backLabel = 'Back' }) => {
  const [step, setStep]         = useState(challenge?.phoneRequired ? 'phone' : 'otp');
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState('');
  const [masked, setMasked]     = useState(challenge?.phoneMasked || '');
  const [cooldown, setCooldown] = useState(challenge?.phoneRequired ? 0 : (challenge?.retryAfterSeconds ?? 60));
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState('');
  const inputRef = useRef(null);

  // Resend cooldown timer (har second 1 kam)
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Step badalne par input par focus
  useEffect(() => { inputRef.current?.focus(); }, [step]);

  // Session khatam ho gaya to parent ko batao
  const handleDead = (result) => {
    if (isSessionDead(result)) { onExpired && onExpired(); return true; }
    return false;
  };

  /* ── Step 1: number do, OTP bhejo ── */
  const handleSendToPhone = async (e) => {
    e.preventDefault();
    if (loading) return;
    const d = digitsOnly(phone).slice(-10);
    if (!/^[6-9]\d{9}$/.test(d)) { setError('Enter a valid 10-digit mobile number'); return; }

    setLoading(true); setError(''); setInfo('');
    const result = await authController.addPhoneForOtp(challenge.challengeId, d);
    setLoading(false);

    if (result.success) {
      setMasked(result.data?.phoneMasked || `******${d.slice(-4)}`);
      setCooldown(result.data?.retryAfterSeconds ?? 60);
      setOtp('');
      setStep('otp');
    } else if (!handleDead(result)) {
      setError(result.message || 'Could not send OTP');
    }
  };

  /* ── Step 2: OTP verify ── */
  const handleVerify = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }

    setLoading(true); setError(''); setInfo('');
    const result = await authController.verifyOtp(challenge.challengeId, otp);
    setLoading(false);

    if (result.success) {
      onVerified && onVerified(result);
    } else if (!handleDead(result)) {
      const left = typeof result.attemptsLeft === 'number' ? ` (${result.attemptsLeft} attempts left)` : '';
      setError((result.message || 'Invalid or expired OTP') + left);
      setOtp('');
    }
  };

  /* ── OTP dobara bhejo ── */
  const handleResend = async () => {
    if (loading || cooldown > 0) return;
    setLoading(true); setError(''); setInfo('');
    const result = await authController.resendOtp(challenge.challengeId);
    setLoading(false);

    if (result.success) {
      setCooldown(result.data?.retryAfterSeconds ?? 60);
      setInfo('OTP sent again');
    } else if (!handleDead(result)) {
      setError(result.message || 'Could not resend OTP');
    }
  };

  const focusOn  = (e) => { e.target.style.borderColor = '#2563eb'; };
  const focusOff = (e) => { e.target.style.borderColor = '#d1d5db'; };

  return (
    <div>
      {step === 'phone' ? (
        <>
          <h2 style={o.title}>Add Mobile Number</h2>
          <p style={o.sub}>OTP verification is required to continue, and your account has no valid mobile number yet. Enter your mobile number — we'll send a 6-digit OTP to verify it.</p>

          {error && <div style={o.error}>{error}</div>}

          <form style={o.form} onSubmit={handleSendToPhone}>
            <div>
              <label style={o.label} htmlFor="otp-phone">Mobile number</label>
              <input
                id="otp-phone" ref={inputRef} type="tel" inputMode="numeric"
                style={o.input} value={phone} placeholder="10-digit mobile number"
                onChange={(e) => { setPhone(digitsOnly(e.target.value).slice(-10)); setError(''); }}
                disabled={loading} onFocus={focusOn} onBlur={focusOff}
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{ ...o.btn, ...(loading ? o.btnDisabled : {}) }}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        </>
      ) : (
        <>
          <h2 style={o.title}>Verify OTP</h2>
          <p style={o.sub}>
            We've sent a 6-digit OTP to <strong>{masked}</strong>.
          </p>

          {error && <div style={o.error}>{error}</div>}
          {info && <div style={o.info}>{info}</div>}

          <form style={o.form} onSubmit={handleVerify}>
            <input
              ref={inputRef} type="text" inputMode="numeric" autoComplete="one-time-code"
              aria-label="One-time password" maxLength={6}
              style={o.otpInput} value={otp} placeholder="••••••"
              onChange={(e) => { setOtp(digitsOnly(e.target.value).slice(0, 6)); setError(''); }}
              disabled={loading} onFocus={focusOn} onBlur={focusOff}
            />
            <button
              type="submit" disabled={loading || otp.length !== 6}
              style={{ ...o.btn, ...(loading || otp.length !== 6 ? o.btnDisabled : {}) }}
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>

            <div style={o.row}>
              <button
                type="button" onClick={handleResend} disabled={loading || cooldown > 0}
                style={{ ...o.linkBtn, ...(loading || cooldown > 0 ? o.linkDisabled : {}) }}
              >
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
              </button>

              {/* Galat number daal diya? Number add karte waqt badal sakte ho */}
              {challenge?.phoneRequired && (
                <button
                  type="button" style={o.linkBtn} disabled={loading}
                  onClick={() => { setStep('phone'); setError(''); setInfo(''); setOtp(''); }}
                >
                  Change number
                </button>
              )}
            </div>
          </form>
        </>
      )}

      <div style={{ textAlign: 'center', marginTop: '18px' }}>
        <button type="button" style={o.linkBtn} onClick={onBack} disabled={loading}>
          {backLabel}
        </button>
      </div>
    </div>
  );
};

export default OtpVerification;